/**
 * The signed-in user's own account: profile, sessions, password.
 *
 * **Live against the real API** — all three are `docs/API.md` §2 and §3, which
 * are implemented.
 *
 * These are not in the prototype; the prototype faked its session in
 * `localStorage` and had nothing to manage. With real sessions they are the
 * minimum a user needs: see where they are signed in, end anything they do not
 * recognise, and change their password.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as authApi from '../../api/auth.api.js';
import * as usersApi from '../../api/users.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { useAuth } from '../../hooks/useAuth.js';
import { pushToast } from '../ui/uiSlice.js';
import { loadSession } from '../auth/authSlice.js';
import { AuthLayout } from '../auth/LoginPage.jsx';
import {
  PageLoader, ButtonSpinner, PageHeader, Card, Badge, Avatar, Field, Spinner, ErrorState, Tabs,
} from '../../components/common/index.jsx';
import { dateTime, relativeTime, date } from '../../utils/formatters.js';

/* -------------------------------------------------------------------------- */
/* Profile                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Your own record.
 *
 * Only the fields the API lets a non-admin change on themselves are editable —
 * mobile, timezone, locale. Everything else is displayed read-only rather than
 * hidden, because "who is my manager" is a question people ask of this screen
 * even when they cannot change the answer.
 */
export function ProfilePage() {
  const { user, roles, organization } = useAuth();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { data: me, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.users.detail('me'),
    queryFn: () => usersApi.getUser('me'),
  });

  const [draft, setDraft] = useState(null);
  const editing = draft !== null;

  const save = useMutation({
    // row_version is what makes this a conditional write: if someone else saved
    // while this form was open the API answers 409 rather than overwriting them.
    mutationFn: () => usersApi.updateUser('me', draft, me.row_version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail('me') });
      dispatch(loadSession());
      dispatch(pushToast({ tone: 'success', message: 'Profile updated' }));
      setDraft(null);
    },
    onError: (err) => {
      if (err.code === 'stale_write') {
        dispatch(pushToast({
          tone: 'warning',
          message: 'Someone else changed your profile',
          detail: 'Reloading so you are editing the current version.',
        }));
        queryClient.invalidateQueries({ queryKey: queryKeys.users.detail('me') });
        setDraft(null);
        return;
      }
      dispatch(pushToast({ tone: 'danger', message: 'Could not save', detail: err.message }));
    },
  });

  if (isLoading) return <PageLoader label="Loading your profile" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  return (
    <>
      <PageHeader title="My profile" subtitle={organization?.name} />

      <div className="grid-2">
        <Card
          title="Details"
          actions={
            editing ? (
              <>
                <button type="button" className="btn btn-cancel btn-sm-x" onClick={() => setDraft(null)}>Cancel</button>
                <button type="button" className="btn btn-save btn-sm-x" onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-edit btn-sm-x"
                onClick={() => setDraft({ mobile: me.mobile ?? '', timezone: me.timezone, locale: me.locale })}
              >
                <i className="bi bi-pencil me-1" aria-hidden="true" />Edit
              </button>
            )
          }
        >
          <div className="d-flex align-items-center gap-3 mb-3">
            <Avatar name={me.full_name} size={56} />
            <div>
              <div className="fw-700 t-md">{me.full_name}</div>
              <div className="t-sm text-muted">{me.email}</div>
            </div>
          </div>

          {editing ? (
            <>
              <Field label="Mobile"><input className="form-control" value={draft.mobile} onChange={set('mobile')} /></Field>
              <Field label="Timezone" help="Affects how dates and times are shown to you.">
                <input className="form-control" value={draft.timezone} onChange={set('timezone')} />
              </Field>
              <Field label="Locale"><input className="form-control" value={draft.locale} onChange={set('locale')} /></Field>
              <p className="t-2xs text-muted mb-0">
                Only these three are editable on your own profile. Designation, team and roles are
                changed by an administrator — a permission change gets its own audited call.
              </p>
            </>
          ) : (
            <dl className="detail-list">
              <div className="detail-row"><dt>Designation</dt><dd>{me.designation ?? '—'}</dd></div>
              <div className="detail-row"><dt>Department</dt><dd>{me.department?.name ?? '—'}</dd></div>
              <div className="detail-row"><dt>Manager</dt><dd>{me.manager?.full_name ?? '—'}</dd></div>
              <div className="detail-row"><dt>Team</dt><dd>{me.primary_team?.name ?? '—'}</dd></div>
              <div className="detail-row"><dt>Mobile</dt><dd>{me.mobile ?? '—'}</dd></div>
              <div className="detail-row"><dt>Timezone</dt><dd>{me.timezone}</dd></div>
              <div className="detail-row"><dt>Locale</dt><dd>{me.locale}</dd></div>
              <div className="detail-row"><dt>Joined</dt><dd>{me.joining_date ? date(me.joining_date) : '—'}</dd></div>
              <div className="detail-row"><dt>Sign-in methods</dt><dd>{me.auth_methods?.join(', ') || 'Password'}</dd></div>
            </dl>
          )}
        </Card>

        <Card title="Your roles" subtitle="What you can see is the union of these, minus any deny">
          <ul className="list-x">
            {roles.map((role) => (
              <li key={role.id ?? role.code} className="list-x-item">
                <div className="flex-grow-1">
                  <div className="fw-600 t-sm">{role.name ?? role.code}</div>
                  <div className="t-xs text-muted">
                    {role.scope ? `${role.scope.kind}: ${role.scope.name}` : 'Unscoped'}
                    {role.source && ` · ${role.source}`}
                  </div>
                </div>
                {role.is_primary && <Badge tone="brand">Primary</Badge>}
              </li>
            ))}
          </ul>
          <p className="t-2xs text-muted mb-0 mt-2">
            Where two roles disagree, a deny wins. Data visibility resolves the other way — the
            widest scope applies.
          </p>
        </Card>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Sessions                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Where you are signed in.
 *
 * Revocation is immediate, not advisory: the API reads the session row on every
 * request, so ending one here stops it working on the next call rather than when
 * its token happens to expire. The current session is marked and cannot be
 * ended from this list — signing out is the button for that, and it is elsewhere.
 */
export function SessionsPage() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.sessions,
    queryFn: authApi.listSessions,
  });

  const revoke = useMutation({
    mutationFn: (id) => authApi.revokeSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
      dispatch(pushToast({ tone: 'success', message: 'Session ended' }));
    },
    onError: (err) => dispatch(pushToast({ tone: 'danger', message: 'Could not end that session', detail: err.message })),
  });

  return (
    <>
      <PageHeader title="Active sessions" subtitle="Everywhere your account is currently signed in." />

      <Card>
        {isLoading ? <Spinner /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
          <ul className="list-x">
            {(data ?? []).map((session) => (
              <li key={session.id} className="list-x-item align-items-start">
                <i className="bi bi-laptop t-lg text-muted mt-1" aria-hidden="true" />
                <div className="flex-grow-1">
                  <div className="fw-600 t-sm">
                    {session.device_label ?? 'Unknown device'}
                    {session.is_current && <Badge tone="emerald" className="ms-2">This device</Badge>}
                  </div>
                  <div className="t-xs text-muted">
                    {session.ip ?? 'unknown IP'}
                    {session.location && ` · ${session.location}`}
                    {' · '}signed in {relativeTime(session.issued_at)}
                  </div>
                  <div className="t-2xs text-muted">
                    Last active {relativeTime(session.last_active_at)} · expires {dateTime(session.absolute_expires_at)}
                    {' · '}method {session.auth_method}
                  </div>
                </div>
                {!session.is_current && (
                  <button
                    type="button"
                    className="btn btn-delete btn-sm-x"
                    onClick={() => revoke.mutate(session.id)}
                    disabled={revoke.isPending}
                  >
                    End session
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Password                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Change your own password.
 *
 * Policy failures come back from the server with `details[]` naming the rule
 * that failed, and they are rendered as a list rather than a single message —
 * "must contain a symbol" is actionable in a way that "does not meet the policy"
 * is not.
 *
 * On success every other session is revoked and the current one survives, so the
 * user is not signed out of the tab they are standing in.
 */
export function ChangePasswordPage() {
  const dispatch = useDispatch();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [violations, setViolations] = useState([]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const mismatch = form.confirm.length > 0 && form.new_password !== form.confirm;

  const change = useMutation({
    mutationFn: () =>
      authApi.changePassword({ current_password: form.current_password, new_password: form.new_password }),
    onSuccess: () => {
      setForm({ current_password: '', new_password: '', confirm: '' });
      setViolations([]);
      dispatch(pushToast({
        tone: 'success',
        message: 'Password changed',
        detail: 'Your other sessions have been signed out.',
      }));
    },
    onError: (err) => {
      if (err.code === 'password_policy_violation') {
        setViolations(err.details ?? []);
        return;
      }
      setViolations([]);
      dispatch(pushToast({
        tone: 'danger',
        message: err.code === 'invalid_credentials' ? 'Your current password is not correct' : 'Could not change your password',
        detail: err.code === 'password_recently_used' ? err.message : undefined,
      }));
    },
  });

  return (
    <>
      <PageHeader title="Change password" subtitle="Your other sessions will be signed out." />

      <Card className="narrow-card">
        {violations.length > 0 && (
          <div className="alert-x alert-danger-x mb-3">
            <div className="fw-600 mb-1">That password does not meet the policy</div>
            <ul className="t-xs mb-0 ps-3">
              {violations.map((v, i) => <li key={i}>{v.message ?? v.code}</li>)}
            </ul>
          </div>
        )}

        <Field label="Current password" required>
          <input type="password" className="form-control" autoComplete="current-password" value={form.current_password} onChange={set('current_password')} />
        </Field>
        <Field label="New password" required help="Your organisation's policy is shown under Administration → Security.">
          <input type="password" className="form-control" autoComplete="new-password" value={form.new_password} onChange={set('new_password')} />
        </Field>
        <Field label="Confirm new password" required error={mismatch ? 'The two passwords do not match.' : undefined}>
          <input type="password" className="form-control" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} />
        </Field>

        <button
          type="button"
          className="btn btn-save"
          disabled={!form.current_password || !form.new_password || mismatch || change.isPending}
          onClick={() => change.mutate()}
        >
          {change.isPending ? 'Changing…' : 'Change password'}
        </button>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Forgot / reset                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Request a reset link.
 *
 * By product decision this now tells the caller outright when an email has
 * no account (`email_not_found`, 404) instead of the enumeration-safe
 * uniform response §2 specifies — see the doc comment on
 * `auth.service.js`'s `forgotPassword` for the tradeoff that was accepted.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const request = useMutation({
    mutationFn: () => authApi.forgotPassword(email),
    onSuccess: () => setSent(true),
  });

  const notFound = request.isError && request.error?.code === 'email_not_found';

  return (
    <AuthLayout>
      <div
        className="d-flex align-items-center justify-content-center mx-auto mb-3"
        style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--grad-brand)', boxShadow: 'var(--shadow-brand)' }}
      >
        <i className={`bi ${sent ? 'bi-envelope-check-fill' : 'bi-key-fill'} text-white`} style={{ fontSize: 22 }} />
      </div>

      {sent ? (
        <>
          <h2 className="fw-750 text-center mb-1" style={{ letterSpacing: '-.035em' }}>Check your email</h2>
          <p className="t-sm ink-3 text-center mb-4">
            A single-use reset link is on its way to <span className="fw-650 ink-2">{email}</span>.
            It expires in 30 minutes.
          </p>
          <div
            className="d-flex align-items-start gap-2 mb-3 p-3"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}
          >
            <i className="bi bi-info-circle text-primary mt-1" />
            <div className="t-xs ink-3">
              No mail transport is configured in this build — the link is written to the API server
              log instead.
            </div>
          </div>
          <a className="btn btn-ghost btn-lg-x w-100" href="/login">Back to sign in</a>
        </>
      ) : (
        <>
          <h2 className="fw-750 text-center mb-1" style={{ letterSpacing: '-.035em' }}>Reset your password</h2>
          <p className="t-sm ink-3 text-center mb-4">
            Enter your work email. We&rsquo;ll send a secure, single-use reset link valid for 30 minutes.
          </p>

          <label className="lbl" htmlFor="forgotEmail">Work email</label>
          <div className="inp-group mb-3">
            <input
              type="email"
              className={`form-control${notFound ? ' is-invalid' : ''}`}
              id="forgotEmail"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); request.reset(); }}
              autoFocus
            />
            <i className="bi bi-envelope inp-ico" />
          </div>
          {notFound ? (
            <div className="t-2xs mb-3" style={{ color: 'var(--rose)' }}>
              No account exists for that email address.
            </div>
          ) : request.isError && (
            <div className="alert alert-danger py-2 px-3 t-xs mb-3" role="alert">
              {request.error.message}
            </div>
          )}

          <button
            type="button"
            className="btn btn-brand btn-lg-x w-100"
            disabled={!email || request.isPending}
            onClick={() => request.mutate()}
          >
            {request.isPending ? <><ButtonSpinner />Sending…</> : 'Send reset link'}
          </button>
          <a className="btn btn-ghost btn-lg-x w-100 mt-2" href="/login">Back to sign in</a>
        </>
      )}
    </AuthLayout>
  );
}

/**
 * Consume the link `forgotPassword` issues and set a new password.
 *
 * This is the step that was missing: `POST /auth/password/forgot` has always
 * worked (it writes a real single-use token, only its delivery is logged
 * instead of emailed — no mail transport in this build), but nothing in the
 * app ever called `POST /auth/password/reset` with that token, so a link with
 * nowhere to land could not actually reset anything. `?token=` is what the
 * logged link carries; paste it into this URL to complete the flow.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [form, setForm] = useState({ new_password: '', confirm: '' });
  const [violations, setViolations] = useState([]);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const mismatch = form.confirm.length > 0 && form.new_password !== form.confirm;

  const reset = useMutation({
    mutationFn: () => authApi.resetPassword({ token, new_password: form.new_password }),
    onSuccess: () => setDone(true),
    onError: (err) => {
      if (err.code === 'password_policy_violation') {
        setViolations(err.details ?? []);
        return;
      }
      setViolations([{ code: err.code, message: err.message }]);
    },
  });

  if (!token) {
    return (
      <AuthLayout>
        <h2 className="fw-750 text-center mb-1" style={{ letterSpacing: '-.035em' }}>Invalid reset link</h2>
        <p className="t-sm ink-3 text-center mb-4">
          This link is missing its token. Request a new one from the sign-in page.
        </p>
        <a className="btn btn-brand btn-lg-x w-100" href="/forgot-password">Request a new link</a>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <div
          className="d-flex align-items-center justify-content-center mx-auto mb-3"
          style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--grad-brand)', boxShadow: 'var(--shadow-brand)' }}
        >
          <i className="bi bi-check-circle-fill text-white" style={{ fontSize: 22 }} />
        </div>
        <h2 className="fw-750 text-center mb-1" style={{ letterSpacing: '-.035em' }}>Password changed</h2>
        <p className="t-sm ink-3 text-center mb-4">
          Every session on your account has been signed out. Sign in again with your new password.
        </p>
        <button type="button" className="btn btn-brand btn-lg-x w-100" onClick={() => navigate('/login', { replace: true })}>
          Back to sign in
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div
        className="d-flex align-items-center justify-content-center mx-auto mb-3"
        style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--grad-brand)', boxShadow: 'var(--shadow-brand)' }}
      >
        <i className="bi bi-key-fill text-white" style={{ fontSize: 22 }} />
      </div>
      <h2 className="fw-750 text-center mb-1" style={{ letterSpacing: '-.035em' }}>Set a new password</h2>
      <p className="t-sm ink-3 text-center mb-4">
        Choose a new password for your account. This link works once.
      </p>

      {violations.length > 0 && (
        <div className="alert-x alert-danger-x mb-3">
          <div className="fw-600 mb-1">That password could not be set</div>
          <ul className="t-xs mb-0 ps-3">
            {violations.map((v, i) => <li key={i}>{v.message ?? v.code}</li>)}
          </ul>
        </div>
      )}

      <Field label="New password" required>
        <input type="password" className="form-control" autoComplete="new-password" autoFocus value={form.new_password} onChange={set('new_password')} />
      </Field>
      <Field label="Confirm new password" required error={mismatch ? 'The two passwords do not match.' : undefined}>
        <input type="password" className="form-control" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} />
      </Field>

      <button
        type="button"
        className="btn btn-brand btn-lg-x w-100 mt-1"
        disabled={!form.new_password || mismatch || reset.isPending}
        onClick={() => reset.mutate()}
      >
        {reset.isPending ? <><ButtonSpinner />Saving…</> : 'Set new password'}
      </button>
      <a className="btn btn-ghost btn-lg-x w-100 mt-2" href="/login">Back to sign in</a>
    </AuthLayout>
  );
}
